import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@talktoast/shared'

export default function AuthCallback() {
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)
  const hasRun = useRef(false)

  useEffect(() => {
    // Guard against React StrictMode double-invoke: only process the callback once.
    if (hasRun.current) return
    hasRun.current = true

    let cancelled = false

    const handleCallback = async () => {
      const params = new URLSearchParams(window.location.search)
      const code = params.get('code')

      if (!code) {
        // Implicit flow: Supabase delivers tokens in the hash fragment.
        // The Supabase client SDK auto-processes these on load, so check if a
        // session already exists before showing an error.
        const hashParams = new URLSearchParams(window.location.hash.substring(1))
        const accessToken = hashParams.get('access_token')
        const errorParam = hashParams.get('error') || params.get('error')

        if (errorParam) {
          if (!cancelled) setError(hashParams.get('error_description') || params.get('error_description') || 'Authentication failed')
          return
        }

        if (accessToken) {
          // Session already established by Supabase SDK from hash — redirect to app.
          if (!cancelled) navigate('/app', { replace: true })
          return
        }

        // Truly no code/token — check if a session was somehow already established.
        const { data: { session } } = await supabase.auth.getSession()
        if (session) {
          if (!cancelled) navigate('/app', { replace: true })
          return
        }

        if (!cancelled) setError('Invalid confirmation link. Please request a new one.')
        return
      }

      try {
        const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(code)
        if (exchangeError) throw exchangeError
        // Success — redirect to the app. Never use a ?next= param (security requirement).
        if (!cancelled) navigate('/app', { replace: true })
      } catch (err) {
        // The code may already have been consumed (e.g. StrictMode double-invoke).
        // If a valid session exists, redirect instead of showing an error.
        const { data: { session } } = await supabase.auth.getSession()
        if (session) {
          if (!cancelled) navigate('/app', { replace: true })
          return
        }
        if (!cancelled) setError(err instanceof Error ? err.message : 'Confirmation failed. Please try again.')
      }
    }

    handleCallback()
    return () => { cancelled = true }
  }, [navigate])

  if (error) {
    return (
      <div className="min-h-screen bg-toast-cream flex flex-col items-center justify-center gap-4 p-4">
        <p className="text-red-700 text-center text-sm">{error}</p>
        <button
          onClick={() => navigate('/app', { replace: true })}
          className="text-toast-gold-700 font-medium hover:text-toast-gold-800 underline text-sm"
        >
          Return to sign in
        </button>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-toast-cream flex flex-col items-center justify-center gap-4">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-toast-gold" />
      <p className="text-toast-brown font-medium">Confirming your account...</p>
    </div>
  )
}
